import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import * as adminApi from '../../../api/adminApi';
import { updateProfile as updateProfileApi, changePassword as changePasswordApi, deleteAccount as deleteAccountApi } from '../../../api/authApi';

export const useAdminForms = (requireToken: () => string | null) => {
  const navigate = useNavigate();

  // Document Upload
  const [documentUploadLoading, setDocumentUploadLoading] = useState(false);
  const [documentError, setDocumentError] = useState<string | null>(null);

  // Transcript
  const [transcriptError, setTranscriptError] = useState<string | null>(null);
  const [newTranscriptUrl, setNewTranscriptUrl] = useState('');
  const [newTranscriptLanguage, setNewTranscriptLanguage] = useState('');
  const [transcriptPreview, setTranscriptPreview] = useState<adminApi.AdminYoutubeTranscriptPreview | null>(null);
  const [transcriptPreviewLoading, setTranscriptPreviewLoading] = useState(false);
  const [transcriptUploadLoading, setTranscriptUploadLoading] = useState(false);
  const [transcriptSuccessMessage, setTranscriptSuccessMessage] = useState<string | null>(null);

  // Admin Creation
  const [newAdmin, setNewAdmin] = useState({ email: '', name: '', password: '', customRoleId: '' });
  const [adminCreationStatus, setAdminCreationStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [adminCreationError, setAdminCreationError] = useState<string | null>(null);
  const [credentialsStatus, setCredentialsStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [credentialsError, setCredentialsError] = useState<string | null>(null);

  // Profile Edit
  const [editingProfileName, setEditingProfileName] = useState(false);
  const [profileNameInput, setProfileNameInput] = useState('');
  const [profileUpdateError, setProfileUpdateError] = useState<string | null>(null);
  const [profileUpdateLoading, setProfileUpdateLoading] = useState(false);

  // Password Change
  const [passwordForm, setPasswordForm] = useState({ current: '', next: '', confirm: '' });
  const [passwordStatus, setPasswordStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [passwordMessage, setPasswordMessage] = useState<string | null>(null);

  // Account Deletion
  const [deleteAccountError, setDeleteAccountError] = useState<string | null>(null);
  const [deleteAccountConfirm, setDeleteAccountConfirm] = useState(false);
  const [deleteAccountLoading, setDeleteAccountLoading] = useState(false);

  // Document Handlers
  const handleDocumentUpload = useCallback(async (event: React.FormEvent<HTMLFormElement>, onSuccess: (doc: adminApi.AdminDocument) => void) => {
    event.preventDefault();
    const authToken = requireToken();
    if (!authToken) return;

    const formElement = event.currentTarget;
    const formData = new FormData(formElement);
    const fileInput = formElement.elements.namedItem('document') as HTMLInputElement;
    const file = fileInput?.files?.[0];
    if (!file) {
      setDocumentError('Please select a file to upload');
      return;
    }

    try {
      setDocumentUploadLoading(true);
      setDocumentError(null);
      const document = await adminApi.uploadAdminDocument(
        authToken,
        file,
        (formData.get('description') as string) || undefined,
      );
      onSuccess(document);
      formElement.reset();
    } catch (err) {
      const status = (err as { status?: number }).status;
      if (status === 401) {
        navigate('/admin/login');
        return;
      }
      setDocumentError((err as Error).message || 'Failed to upload document');
    } finally {
      setDocumentUploadLoading(false);
    }
  }, [requireToken, navigate]);

  // Transcript Handlers
  const handleTranscriptCreate = useCallback(async (event: React.FormEvent) => {
    event.preventDefault();
    const authToken = requireToken();
    if (!authToken) return;
    if (!newTranscriptUrl) {
      setTranscriptError('Please enter a YouTube URL');
      return;
    }

    try {
      setTranscriptError(null);
      setTranscriptSuccessMessage(null);
      setTranscriptPreview(null);
      setTranscriptPreviewLoading(true);
      const preview = await adminApi.previewAdminYoutubeTranscript(authToken, {
        youtube_url: newTranscriptUrl,
        language: newTranscriptLanguage || undefined,
      });
      setTranscriptPreview(preview);
    } catch (err) {
      const status = (err as { status?: number }).status;
      if (status === 401) {
        navigate('/admin/login');
      } else {
        let errorMessage = (err as Error).message || 'Failed to fetch transcript';
        if (status === 503 && errorMessage.includes('YouTube')) {
          errorMessage =
            'YouTube transcript retrieval is unavailable. The cloud server requires a YouTube API key to download transcripts. Please contact support to enable this feature.';
        } else if (status === 503 && errorMessage.includes('blocking')) {
          errorMessage =
            'YouTube is blocking requests from this server IP. Please contact support to configure a YouTube API key for reliable transcript generation.';
        }
        setTranscriptError(errorMessage);
      }
    } finally {
      setTranscriptPreviewLoading(false);
    }
  }, [requireToken, navigate, newTranscriptUrl, newTranscriptLanguage]);

  const handleTranscriptUpload = useCallback(async (onSuccess: (transcript: adminApi.AdminYoutubeTranscript) => void) => {
    const authToken = requireToken();
    if (!authToken || !transcriptPreview) return;

    try {
      setTranscriptUploadLoading(true);
      setTranscriptError(null);
      const transcript = await adminApi.createAdminYoutubeTranscript(authToken, {
        youtube_url: transcriptPreview.youtube_url,
        language: transcriptPreview.language || undefined,
        transcript_text: transcriptPreview.transcript_text,
        detected_language: transcriptPreview.language || undefined,
      });
      onSuccess(transcript);
      setTranscriptSuccessMessage('Transcript uploaded to storage.');
      setTranscriptPreview(null);
      setNewTranscriptUrl('');
      setNewTranscriptLanguage('');
    } catch (err) {
      const status = (err as { status?: number }).status;
      if (status === 401) {
        navigate('/admin/login');
      } else {
        setTranscriptError((err as Error).message || 'Failed to upload transcript');
      }
    } finally {
      setTranscriptUploadLoading(false);
    }
  }, [requireToken, navigate, transcriptPreview]);

  // Admin Handlers
  const handleAdminInputChange = useCallback((event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = event.target;
    setNewAdmin(prev => ({ ...prev, [name]: value }));
    setAdminCreationError(null);
    setAdminCreationStatus('idle');
  }, []);

  const handleAdminCreate = useCallback(async (event: React.FormEvent<HTMLFormElement>, onSuccess: () => void) => {
    event.preventDefault();
    const authToken = requireToken();
    if (!authToken) return;

    if (!newAdmin.email || !newAdmin.password || !newAdmin.name) {
      setAdminCreationError('All fields are required');
      return;
    }

    try {
      setAdminCreationStatus('idle');
      setAdminCreationError(null);
      await adminApi.createAdmin(authToken, {
        email: newAdmin.email,
        password: newAdmin.password,
        full_name: newAdmin.name,
        custom_role_id: newAdmin.customRoleId ? Number(newAdmin.customRoleId) : undefined,
      });
      setAdminCreationStatus('success');
      setNewAdmin({ email: '', name: '', password: '', customRoleId: '' });
      onSuccess();
    } catch (err) {
      setAdminCreationStatus('error');
      setAdminCreationError((err as Error).message);
    }
  }, [requireToken, newAdmin]);

  const handleSendAdminCredentials = useCallback(async () => {
    const authToken = requireToken();
    if (!authToken) return;

    if (!newAdmin.email || !newAdmin.password || !newAdmin.name) {
      setCredentialsStatus('error');
      setCredentialsError('Fill out email, name, and password first');
      return;
    }

    try {
      setCredentialsStatus('loading');
      setCredentialsError(null);
      await adminApi.sendAdminCredentials(authToken, {
        email: newAdmin.email,
        password: newAdmin.password,
        full_name: newAdmin.name,
      });
      setCredentialsStatus('success');
    } catch (err) {
      setCredentialsStatus('error');
      setCredentialsError((err as Error).message);
    }
  }, [requireToken, newAdmin]);

  // Profile Handlers
  const handleProfileEditStart = useCallback((currentName: string) => {
    setProfileNameInput(currentName);
    setEditingProfileName(true);
    setProfileUpdateError(null);
  }, []);

  const handleProfileEditCancel = useCallback(() => {
    setEditingProfileName(false);
    setProfileUpdateError(null);
    setProfileUpdateLoading(false);
  }, []);

  const handleProfileSave = useCallback(async (event: React.FormEvent<HTMLFormElement>, onSuccess: (updated: any) => void) => {
    event.preventDefault();
    const authToken = requireToken();
    if (!authToken) return;

    const trimmed = profileNameInput.trim();
    if (!trimmed) {
      setProfileUpdateError('Name cannot be empty.');
      return;
    }

    try {
      setProfileUpdateLoading(true);
      setProfileUpdateError(null);
      const updated = await updateProfileApi(authToken, { full_name: trimmed });
      onSuccess(updated);
      setEditingProfileName(false);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to update profile';
      setProfileUpdateError(message);
    } finally {
      setProfileUpdateLoading(false);
    }
  }, [requireToken, profileNameInput]);

  // Password Handlers
  const handlePasswordInputChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    setPasswordForm(prev => ({ ...prev, [name]: value }));
    setPasswordStatus('idle');
    setPasswordMessage(null);
  }, []);

  const handlePasswordSubmit = useCallback(async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const authToken = requireToken();
    if (!authToken) return;

    if (!passwordForm.current || !passwordForm.next || !passwordForm.confirm) {
      setPasswordStatus('error');
      setPasswordMessage('All fields are required.');
      return;
    }

    if (passwordForm.next !== passwordForm.confirm) {
      setPasswordStatus('error');
      setPasswordMessage('New passwords do not match.');
      return;
    }

    try {
      setPasswordStatus('loading');
      setPasswordMessage(null);
      await changePasswordApi(authToken, {
        current_password: passwordForm.current,
        new_password: passwordForm.next,
      });
      setPasswordStatus('success');
      setPasswordMessage('Password updated successfully.');
      setPasswordForm({ current: '', next: '', confirm: '' });
    } catch (err) {
      const status = (err as { status?: number }).status;
      if (status === 401) {
        navigate('/admin/login');
        return;
      }
      const message = err instanceof Error ? err.message : 'Failed to update password';
      setPasswordStatus('error');
      setPasswordMessage(message || 'Failed to update password');
    }
  }, [requireToken, navigate, passwordForm]);

  // Account Deletion
  const handleAdminDeleteAccount = useCallback(async () => {
    const authToken = requireToken();
    if (!authToken) return;

    setDeleteAccountError(null);
    setDeleteAccountLoading(true);
    try {
      const result = await deleteAccountApi(authToken);
      setDeleteAccountConfirm(false);
      setDeleteAccountError(result.detail || 'A confirmation email has been sent to your inbox.');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to request account deletion';
      setDeleteAccountError(message);
    } finally {
      setDeleteAccountLoading(false);
    }
  }, [requireToken, setDeleteAccountConfirm]);

  return {
    // Document
    documentUploadLoading,
    documentError,
    handleDocumentUpload,
    // Transcript
    transcriptError,
    newTranscriptUrl,
    setNewTranscriptUrl,
    newTranscriptLanguage,
    setNewTranscriptLanguage,
    transcriptPreview,
    setTranscriptPreview,
    transcriptPreviewLoading,
    transcriptUploadLoading,
    transcriptSuccessMessage,
    setTranscriptSuccessMessage,
    handleTranscriptCreate,
    handleTranscriptUpload,
    // Admin
    newAdmin,
    adminCreationStatus,
    adminCreationError,
    credentialsStatus,
    credentialsError,
    handleAdminInputChange,
    handleAdminCreate,
    handleSendAdminCredentials,
    // Profile
    editingProfileName,
    profileNameInput,
    setProfileNameInput,
    profileUpdateError,
    profileUpdateLoading,
    handleProfileEditStart,
    handleProfileEditCancel,
    handleProfileSave,
    // Password
    passwordForm,
    passwordStatus,
    passwordMessage,
    handlePasswordInputChange,
    handlePasswordSubmit,
    // Account
    deleteAccountError,
    deleteAccountConfirm,
    setDeleteAccountConfirm,
    deleteAccountLoading,
    handleAdminDeleteAccount,
  };
};
